'use client'

import { useSetAccountOpenDrawer } from '@/feature/account/components/account-drawer/AccountDrawer.store'
import { toLoginPage } from '@/feature/account/services/account.routing'
import { isSignedOutCartVisibleAtom } from '@/feature/cart/services/cart.store'
import { toFavoritesList } from '@/feature/favorite/services/favorite.routing'
import { openMobileMenuSetAtom } from '@/feature/navigation/components/mobile-menu/MobileMenu.client'
import { openPointOfSaleDrawerAtom } from '@/feature/point-of-sale/services/pointOfSale.store'
import { useStoreLocatorUrl } from '@/feature/store-locator'
import { twMerge } from '@/lib/classname/twMerge'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { useRouter } from '@/lib/navigation/PageNavigation.client'
import { pushToGtmDataLayer } from '@/lib/tracking/services/tracking.gtm'
import { Button } from '@/ui/buttons/button/Button.client'
import { Icon } from '@/ui/icons/icon/Icon'
import { Link } from '@/ui/links/link/Link.client'
import { gql } from '@gql-generated/gql'
import {
  type HeaderButtonsV2_ConfigFragment,
  type SignedInPointOfSaleButton_PickupPointOfSaleFragment,
} from '@gql-generated/graphql'
import { useSetAtom } from 'jotai'
import { useCallback, useMemo } from 'react'

// #region Point Of Sale

function SignedOutPointOfSaleButton({ className, link }: WithClassName & { link: string }) {
  const { t } = useTranslation(['header', 'siteMap'])

  return (
    <Link
      bold
      variant="button-quaternary"
      size="lg"
      isRound={useMemo(() => ({ mobile: true, tablet: true, desktop: false }), [])}
      iconLeft={<Icon.Store />}
      href={link}
      opensInNewTab
      className={className}
      onPress={() =>
        pushToGtmDataLayer({
          event: 'gaEvent',
          eventCategory: 'header',
          eventAction: 'clic déconnecté',
          eventLabel: 'choisissez votre magasin',
        })
      }
    >
      {t('header:findYourPointOfSale')}
    </Link>
  )
}

gql(/* GraphQL */ `
  fragment SignedInPointOfSaleButton_pickupPointOfSale on PointOfSale {
    id
    name
  }
`)

type SignedInPointOfSaleButtonProps = WithClassName<{
  pickupPointOfSale: SignedInPointOfSaleButton_PickupPointOfSaleFragment
  config: HeaderButtonsV2_ConfigFragment
  hasMoreThanOnePointOfSale: boolean
}>

const isHeaderButtonRound = { mobile: true, tablet: true, desktop: false }

function SignedInPointOfSaleButton({
  pickupPointOfSale,
  config,
  hasMoreThanOnePointOfSale,
  className,
}: SignedInPointOfSaleButtonProps) {
  const { t } = useTranslation(['header'])
  const openPointOfSaleDrawer = useSetAtom(openPointOfSaleDrawerAtom)

  const onPointOfSaleButtonPressed = useCallback(() => {
    if (!pickupPointOfSale?.id) return
    openPointOfSaleDrawer({
      pointOfSaleId: pickupPointOfSale.id,
      changeStoreAllowed:
        !!config?.pointOfSale?.isPickupPointOfSaleEnabled && hasMoreThanOnePointOfSale,
    })
    pushToGtmDataLayer({
      event: 'gaEvent',
      eventCategory: 'header',
      eventAction: 'clic connecté',
      eventLabel: 'magasin',
    })
  }, [
    openPointOfSaleDrawer,
    pickupPointOfSale?.id,
    config?.pointOfSale?.isPickupPointOfSaleEnabled,
    hasMoreThanOnePointOfSale,
  ])

  if (!pickupPointOfSale?.name) {
    return null
  }

  return (
    <Button
      variant="quaternary"
      size="lg"
      isRound={isHeaderButtonRound}
      iconLeft={<Icon.Store />}
      aria-label={t('header:myPointOfSale', { pointOfSaleName: pickupPointOfSale.name })}
      onPress={onPointOfSaleButtonPressed}
      className={className}
    >
      {pickupPointOfSale.name}
    </Button>
  )
}

function SignedInWithoutPointOfSaleButton({ className }: WithClassName) {
  const { t } = useTranslation(['header'])

  return (
    <Button
      variant="quaternary"
      size="lg"
      iconLeft={<Icon.Store />}
      isRound={isHeaderButtonRound}
      className={twMerge(className, 'min-w-full max-w-[15rem] desktop:min-w-[auto]')}
    >
      {t('header:findYourPointOfSale')}
    </Button>
  )
}

export function PointOfSaleButton({
  hasMoreThanOnePointOfSale,
  config,
  pickupPointOfSale: pickUpPointOfSale,
  isSignedIn,
  className,
}: WithClassName & {
  hasMoreThanOnePointOfSale: boolean
  config: HeaderButtonsV2_ConfigFragment
  pickupPointOfSale?: SignedInPointOfSaleButton_PickupPointOfSaleFragment | null
  isSignedIn: boolean
}) {
  const storeLocatorUrl = useStoreLocatorUrl()

  if (!isSignedIn) {
    if (!storeLocatorUrl) {
      return null
    }

    return <SignedOutPointOfSaleButton className={className} link={storeLocatorUrl} />
  }

  if (!pickUpPointOfSale) {
    return <SignedInWithoutPointOfSaleButton className={className} />
  }

  return (
    <SignedInPointOfSaleButton
      pickupPointOfSale={pickUpPointOfSale}
      config={config}
      hasMoreThanOnePointOfSale={hasMoreThanOnePointOfSale}
      className={className}
    />
  )
}

// #endregion Point Of Sale

// #region Favorites

export function FavoritesButton({ className }: WithClassName) {
  const { t } = useTranslation(['header'])
  const { pushWithProgressBar } = useRouter()

  return (
    <Button
      variant="quaternary"
      size="lg"
      isRound={isHeaderButtonRound}
      onPress={useCallback(() => {
        pushToGtmDataLayer({
          event: 'gaEvent',
          eventCategory: 'header',
          eventAction: 'clic connecté',
          eventLabel: 'mes listes',
        })
        pushWithProgressBar(toFavoritesList)
      }, [pushWithProgressBar])}
      iconLeft={<Icon.Heart />}
      className={twMerge(className, 'hidden desktop:flex')}
    >
      {t('header:favorites')}
    </Button>
  )
}

// #endregion Favorites

// #region Account

export function SignedInAccountButton({ className }: WithClassName) {
  const { t } = useTranslation(['header'])

  const setAccountOpenDrawer = useSetAccountOpenDrawer()

  return (
    <Button
      variant="quaternary"
      size="lg"
      iconLeft={<Icon.User />}
      isRound={isHeaderButtonRound}
      notification
      notificationClassName="desktop:hidden"
      className={className}
      aria-label={t('header:myAccount')}
      onPress={useCallback(() => {
        pushToGtmDataLayer({
          event: 'gaEvent',
          eventCategory: 'header',
          eventAction: 'clic connecté',
          eventLabel: 'mon compte',
        })
        setAccountOpenDrawer(true)
      }, [setAccountOpenDrawer])}
    >
      {t('header:myAccount')}
    </Button>
  )
}

export function SignedOutAccountButton({ className }: WithClassName) {
  const { t } = useTranslation(['header'])

  return (
    <Link
      bold
      variant="button-quaternary"
      size="lg"
      href={toLoginPage()}
      iconLeft={<Icon.User />}
      isRound={isHeaderButtonRound}
      className={className}
      onPress={useCallback(() => {
        pushToGtmDataLayer({
          event: 'gaEvent',
          eventCategory: 'header',
          eventAction: 'clic déconnecté',
          eventLabel: 'connectez-vous ou créez votre compte',
        })
      }, [])}
    >
      {t('header:login')}
    </Link>
  )
}

// #endregion Account

// #region Cart

export function SignedOutCartButton({ className }: WithClassName) {
  const { t } = useTranslation(['header'])
  const setLoggedOutCartAtomState = useSetAtom(isSignedOutCartVisibleAtom)

  return (
    <Button
      variant="quaternary"
      iconLeft={<Icon.ShoppingBasket />}
      size="lg"
      isRound={isHeaderButtonRound}
      className={className}
      onPress={useCallback(() => {
        setLoggedOutCartAtomState(true)
        pushToGtmDataLayer({
          event: 'gaEvent',
          eventCategory: 'header',
          eventAction: 'clic déconnecté',
          eventLabel: 'panier',
        })
      }, [setLoggedOutCartAtomState])}
    >
      {t('header:cart')}
    </Button>
  )
}

// #endregion Cart

// #region Burger Menu

export function BurgerMenuButton({ className }: WithClassName) {
  const { t } = useTranslation(['header'])

  const openMobileMenu = useSetAtom(openMobileMenuSetAtom)

  return (
    <Button
      size="md"
      isRound
      variant="quaternary"
      iconLeft={<Icon.Menu />}
      onPress={useCallback(() => {
        openMobileMenu()
        pushToGtmDataLayer({
          event: 'gaEvent',
          eventCategory: 'menu',
          eventAction: 'mobile',
          eventLabel: 'clic menu burger',
        })
      }, [openMobileMenu])}
      className={twMerge(className, 'desktop:hidden')}
    >
      {t('header:menu')}
    </Button>
  )
}

// #endregion Burger Menu
